import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import differenceInDays from 'date-fns/difference_in_days';
import { FormattedMessage, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleRight } from '@fortawesome/free-solid-svg-icons';


import env from 'Env';
import ProductCover from 'Components/Utility/ProductCover';
import LinkTag from 'Components/Carousel/LinkTag';
import store from 'Redux/store';
import TagSection from 'Components/Carousel/FeaturedCarouselPanels/PanelComponents/TagSection';
import PlatformIcons from 'Components/Carousel/FeaturedCarouselPanels/PanelComponents/PlatformIcons';
import AddToCartBtn from 'Components/Cards/AddToCartBtn';
import PriceContainer from 'Containers/Utility/Price/PriceContainer';
import CountdownTimer from 'Components/Utility/CountdownTimer';
import NewCountdownTimer from 'Components/Utility/CountdownTimer/CountdownTimer';
import FlashCardProgress from 'Components/FlashSales/FlashCardProgress';

import 'Components/Carousel/CardPanelCarousel/CarouselCard.scss';

export default function CarouselCard({
  card,
  size,
  skeleton,
  cardNumber,
  fantasyVerse,
  FeaturedTopSellerCarousel,
}) {
  const [isHovering, setIsHovering] = useState(false);

  const { formatDate } = useIntl();

  if (skeleton) {
    return (
      <article className={classNames('CarouselCard', { 'CarouselCard--large': size === 'large' })}>
        <div className="CarouselCard__cover" />
        <div className="CarouselCard__details" />
      </article>
    );
  }

  const logClick = (secondaryContext = null) => {
    store.dispatch({
      type: 'frontend/carousel/CLICKED',
      payload: { advertId: card._id, url: card.url, index: cardNumber, component: 'CardPanelCarousel' },
    });

    const section = fantasyVerse ? 'FantasyVerseCarousel' : 'MainCarousel';
    store.dispatch({
      type: 'frontend/click/CLICK_INTERACTION',
      payload: { section, value: card.url, context: `Card${cardNumber}`, secondaryContext },
    });
  };

  const scoreThreshold = env.percentRecommendedThreshold;

  const slideUrl = get(card, 'url', '');
  const slideExternalLink = get(card, 'external_link', false);
  const slideHit = get(card, 'hit', {});
  const slideHits = get(card, 'hits', []);
  const slideType = get(card, 'type', '');
  const slideImgAltText = get(card, 'img_alt_text', '');
  let slideName = get(card, 'name', '');
  let slideProductType = 'game';

  const slideHitBundleItems = get(slideHit, 'bundle_covers', []);
  const slideHitCategories = get(slideHit, 'categories', []);
  const slideHitFreeToPlay = get(slideHit, 'free_to_play', false);
  const slideHitHidePrice = get(slideHit, 'hide_price', false);
  const slideHitMetacriticScore = get(slideHit, 'metacritic_score', 0);
  const slideHitName = get(slideHit, 'name', '');
  const slideHitOpencriticScore = get(slideHit, 'opencritic_score', 0);
  const slideHitPayWhatYouWant = get(slideHit, 'pay_what_you_want', false);
  const slideHitPreorder = get(slideHit, 'preorder', false);
  const slideHitPresale = get(slideHit, 'presale', false);
  const slideHitProductId = get(slideHit, 'product_id', '');
  const slideHitRatingScore = get(slideHit, 'rating_score', 0);
  const slideHitSlug = get(slideHit, 'slug', '');
  const slideHitTiered = get(slideHit, 'tiered', false);
  const slideHitType = get(slideHit, 'type', '');
  const slideHitVoucherToAdd = get(slideHit, 'addVoucher', null);
  const slideHitDiscountPercent = get(slideHit, 'discount_percent', 0);
  const slideHitPrice = get(slideHit, 'price', {});
  const slideHitHideDiscount = get(slideHit, 'hide_discount', false);
  const slideHitDiscountPercentOverride = get(slideHit, 'discount_percent_override', 0);
  const slideHitFullPrice = get(slideHit, 'fullPrice', {});
  const slideHitMystery = get(slideHit, 'mystery', false);
  const slideHitDrm = get(slideHit, 'drm', []);
  const slideHitKeysAvailablePercentage = get(slideHit, 'keysAvailablePercentage', 0);
  const slideHitAvailableUntil = get(slideHit, 'available_valid_until', 0);
  const slideHitAvailableFrom = get(slideHit, 'available_valid_from', 0);
  const slideHitCoverAltText = get(slideHit, 'cover_alt_text', '');
  const slideHitDisplayType = get(slideHit, 'display_type', 'game');
  const slideHitFlashDeal = get(slideHit, 'flash_sale', false);
  const slideHitReleaseDate = get(slideHit, 'release_date', 0);

  if ((slideType === 'product' || slideType === 'bundle' || slideType === 'starDeal' || slideType === 'preOrder') && slideHit && slideHitName) {
    slideName = slideHitName;
  }

  if (slideType === 'product' && !isEmpty(slideHit)) {
    slideProductType = slideHitDisplayType;
  }

  let coverAltText = `Product cover for ${slideName}`;
  if (slideHitCoverAltText) coverAltText = slideHitCoverAltText;
  if (slideImgAltText) coverAltText = slideImgAltText;

  // Will be used to evaluting showing user tags or review data
  const showReviews = !slideHitPreorder && (slideHitRatingScore || slideHitMetacriticScore >= scoreThreshold || slideHitOpencriticScore >= scoreThreshold); // eslint-disable-line max-len
  const showMetacritic = slideHitMetacriticScore >= scoreThreshold;
  const showOpencritic = slideHitOpencriticScore >= scoreThreshold;

  let validDrm = '';
  let filteredDrm = [];
  if (slideHit && slideHitDrm) filteredDrm = slideHitDrm.filter(d => d !== 'voucher');
  if (filteredDrm.length === 1) validDrm = filteredDrm[0];

  let showAddButton = false;
  if ((slideType === 'product' || slideType === 'bundle' || slideType === 'starDeal' || slideType === 'preOrder' || slideType === 'flashDeal') && !slideHitTiered && !slideHitPresale && !slideHitFreeToPlay && !slideHitPayWhatYouWant && slideHitType !== 'pick-and-mix') {
    showAddButton = true;
  }

  let showPrice = false;
  if ((slideType === 'product' || slideType === 'bundle' || slideType === 'starDeal' || slideType === 'preOrder' || slideType === 'flashDeal') && !slideHitPresale && !slideHitFreeToPlay && !slideHitHidePrice) {
    showPrice = true;
  }

  let showPlatforms = false;
  if (slideType === 'product' || slideType === 'bundle' || slideType === 'starDeal' || slideType === 'preOrder' || slideType === 'flashDeal') {
    showPlatforms = true;
  }

  let showTags = false;
  if (slideType === 'product' || slideType === 'bundle' || slideType === 'preOrder' || slideType === 'flashDeal') {
    showTags = true;
  }

  let showBundleItems = false;
  if (slideType === 'bundle' && !isEmpty(slideHitBundleItems) && !slideHitMystery) {
    showBundleItems = true;
  }

  let showLabel = false;
  if (slideType === 'categorySale' || slideType === 'siteWideSale' || slideType === 'publisherSale' || slideType === 'franchiseSale') {
    showLabel = true;
  }

  let showSaleItems = false;
  if ((slideType === 'categorySale' || slideType === 'siteWideSale' || slideType === 'publisherSale' || slideType === 'franchiseSale') && !isEmpty(slideHits)) {
    showSaleItems = true;
  }

  const styling = {
    keys: {
      width: `${slideHitKeysAvailablePercentage}%`,
    },
  };

  let sizesOverride = '(min-width: 1520px) 307px, (min-width: 1200px) 263px, (min-width: 992px) 304px, (min-width: 768px) 344px, (min-width: 576px) 254px, 50vw';
  if (size === 'large') {
    sizesOverride = '(min-width: 1520px) 646px, (min-width: 1200px) 550px, (min-width: 992px) 624px, (min-width: 768px) 704px, (min-width: 576px) 524px, 95vw';
  }

  const daysLeft = differenceInDays(new Date(slideHitAvailableUntil * 1000), new Date());
  const showTimer = slideType === 'bundle' && !slideHitFlashDeal && daysLeft < 50;

  return (
    <article
      className={classNames(
        'CarouselCard faux-block-link',
        { 'CarouselCard--large': size === 'large' },
        { 'CarouselCard--hovering': isHovering },
      )}
      onMouseEnter={() => setIsHovering(FeaturedTopSellerCarousel)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {size !== 'large' && slideHitFlashDeal &&
        <div className="CarouselCard__label CarouselCard__label--flashDeal">
          <div className="CarouselCard__label__content">
            <div className="CarouselCard__label__content__main">
              <FormattedMessage
                id="carousel-card.timer.time-left"
                defaultMessage="Ends in"
              />
              <CountdownTimer showZero value={(slideHitAvailableUntil * 1000)} />
            </div>
            <div className="CarouselCard__label__content__slash" />
          </div>
          <div className="CarouselCard__label__angle" />
        </div>
      }

      {slideType === 'starDeal' &&
        <div className="CarouselCard__label CarouselCard__label--starDeal">
          <div className="CarouselCard__label__content">
            <div className="CarouselCard__label__content__main">
              Star Deal
            </div>
            <div className="CarouselCard__label__content__slash" />
          </div>
          <div className="CarouselCard__label__angle" />
        </div>
      }

      {slideType === 'preOrder' &&
        <div className="CarouselCard__label CarouselCard__label--preOrder">
          <div className="CarouselCard__label__content">
            <div className="CarouselCard__label__content__main">
              {!slideHitReleaseDate &&
                <FormattedMessage
                  id="carousel-card.label.preorder"
                  defaultMessage="Pre-order"
                />
              }
              {slideHitReleaseDate && slideHitSlug !== 'monster-hunter-wilds' &&
                <FormattedMessage
                  id="carousel-card.label.releases-date"
                  defaultMessage="Releases {date}"
                  values={{
                    date: formatDate(new Date(slideHitReleaseDate * 1000), { month: 'short', day: '2-digit' }),
                  }}
                />
              }
              {slideHitReleaseDate && slideHitSlug === 'monster-hunter-wilds' &&
                <FormattedMessage
                  id="carousel-card.label.releases-date.monster"
                  defaultMessage="Releases {date} - Preload Now!"
                  values={{
                    date: formatDate(new Date(slideHitReleaseDate * 1000), { month: 'short', day: '2-digit' }),
                  }}
                />
              }
            </div>
            <div className="CarouselCard__label__content__slash" />
          </div>
          <div className="CarouselCard__label__angle" />
        </div>
      }

      <LinkTag
        url={slideUrl}
        externalLink={slideExternalLink}
        className="faux-block-link__overlay-link"
        onClick={() => logClick()}
      >
        <div className="CarouselCard__coverContainer">
          <ProductCover
            cover={card.img}
            alt={coverAltText}
            displayType={slideProductType}
            sizesOverride={sizesOverride}
          />
          <div className="CarouselCard__coverContainer__overlay">
            {slideName}
          </div>
        </div>
      </LinkTag>

      <div className="CarouselCard__details">
        {size === 'large' &&
          <div className="CarouselCard__details__nameScores">
            <div className="CarouselCard__details__nameScores__name">
              {slideName}
            </div>
            {showReviews &&
              <div className="CarouselCard__details__nameScores__scores">
                {slideHitRatingScore >= 2 &&
                  <div className="CarouselCard__details__nameScores__scores__star">
                    <div className="CarouselCard__details__nameScores__scores__star__number">
                      {slideHitRatingScore}/5
                    </div>
                    <div className="CarouselCard__details__nameScores__scores__star__img">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 90 90" preserveAspectRatio="xMinYMid meet" role="img" aria-label="User review star">
                        <polygon points="45 0 58.91 29.63 90 34.38 67.5 57.44 72.81 90 45 74.63 17.19 90 22.5 57.44 0 34.38 31.09 29.63 45 0" />
                      </svg>
                    </div>
                  </div>
                }

                {showMetacritic &&
                  <div className="CarouselCard__details__nameScores__scores__critic">
                    <div className="CarouselCard__details__nameScores__scores__critic__score">
                      {slideHitMetacriticScore}
                    </div>
                    <div className="CarouselCard__details__nameScores__scores__critic__img">
                      <img src="https://cdn.fanatical.com/production/brand/metacritic/svg/metacritic-icon.svg" alt="metacritic" />
                    </div>
                  </div>
                }

                {!showMetacritic && showOpencritic &&
                  <div className="CarouselCard__details__nameScores__scores__critic">
                    <div className="CarouselCard__details__nameScores__scores__critic__score">
                      {slideHitOpencriticScore}
                    </div>
                    <div className="CarouselCard__details__nameScores__scores__critic__img">
                      <img src="https://cdn.fanatical.com/production/brand/opencritic/svg/opencritic-icon.svg" alt="opencritic" />
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        }

        {size === 'large' && showTags &&
          <TagSection
            tags={slideHitCategories.slice(0, (window.locale !== 'en' ? 3 : 4))}
            clickable={false}
          />
        }

        {size === 'large' && slideHitFlashDeal &&
          <div className="CarouselCard__details__flashDeal">
            <FlashCardProgress
              startingDate={slideHitAvailableFrom}
              endingDate={slideHitAvailableUntil}
            />
          </div>
        }

        {size === 'large' && slideType === 'starDeal' &&
          <div className="CarouselCard__details__starDeal">
            <CountdownTimer featuredDeal value={slideHitAvailableUntil * 1000} />

            <div className="keys-CarouselCard__details__starDeal__keys">
              <small className="CarouselCard__details__starDeal__left">0%</small>
              <div className="progress">
                <div
                  className="progress-bar progress-bar-striped progress-bar-animated"
                  role="progressbar"
                  style={styling.keys}
                  aria-valuenow={slideHitKeysAvailablePercentage}
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-label="Keys remaining"
                  title="Keys remaining"
                />
                {slideHitKeysAvailablePercentage >= 85 &&
                  <div className="progress-timer progress-timer-overlay">
                    {slideHitKeysAvailablePercentage}% &nbsp;<FormattedMessage id="carousel-card.remaininghigh" defaultMessage="Remaining" tagName="span" />
                  </div>
                }
                {slideHitKeysAvailablePercentage < 85 && slideHitKeysAvailablePercentage >= 20 &&
                  <div className="progress-timer progress-timer-overlay">
                    {slideHitKeysAvailablePercentage}% &nbsp;<FormattedMessage id="carousel-card.remaining" defaultMessage="keys left" tagName="span" />
                  </div>
                }
                {slideHitKeysAvailablePercentage < 20 &&
                  <div className="remaining-keys progress-timer progress-timer-overlay">
                    <span className="d-none d-lg-block"><FormattedMessage id="carousel-card.remaininglow" defaultMessage="Last Few Keys Remaining" tagName="span" /></span>
                    <span className="d-none d-md-block d-lg-none"><FormattedMessage id="carousel-card.remaininglowsmall" defaultMessage="Last Few Keys" tagName="span" /></span>
                  </div>
                }
              </div>
              <small className="CarouselCard__details__starDeal__right">100%</small>
            </div>
          </div>
        }

        {size === 'large' && slideHitMystery &&
          <div className="CarouselCard__details__bundleItems">
            <FormattedMessage
              id="carousel-card.mystery.titles"
              defaultMessage="Dive into epic gaming adventures and uncover hidden gems with the {slideHitName}."
              values={{
                slideHitName,
              }}
            />
          </div>
        }

        {size === 'large' && showBundleItems &&
          <div className="CarouselCard__details__bundleItems">
            <FormattedMessage
              id="carousel-card.bundle.titles"
              defaultMessage="{number} Titles including:"
              values={{
                number: slideHitBundleItems.length,
              }}
            />&nbsp;
            {slideHitBundleItems.slice(0, 2).map((item, i) =>
              <React.Fragment key={item.cover}>
                <span>{item.name}</span>
                {i < 1 &&
                  <span>, </span>
                }
              </React.Fragment>,
            )}&nbsp;
            <FormattedMessage
              id="carousel-card.bundle.more"
              defaultMessage="and more."
            />
          </div>
        }

        {size === 'large' && showSaleItems &&
          <div className="CarouselCard__details__bundleItems">
            <FormattedMessage
              id="carousel-card.sales.titles"
              defaultMessage="Including:"
            />&nbsp;
            {slideHits.slice(0, 3).map((item, i) =>
              <React.Fragment key={item.cover}>
                <span>{item.name}</span>
                {i < 2 &&
                  <span>, </span>
                }
              </React.Fragment>,
            )}&nbsp;
            <FormattedMessage
              id="carousel-card.sales.more"
              defaultMessage="and more."
            />
          </div>
        }

        <div className="CarouselCard__details__iconsPrice">
          <div className="CarouselCard__details__iconsPrice__priceButton">
            {showPrice && !showTimer &&
              <PriceContainer
                type={slideHitType}
                discountPercent={slideHitDiscountPercent}
                price={slideHitPrice}
                tiered={slideHitTiered}
                hideDiscount={slideHitHideDiscount}
                slug={slideHitSlug}
                discountPercentOverride={slideHitDiscountPercentOverride}
                fullPrice={slideHitFullPrice}
                mystery={slideHitMystery}
                drm={validDrm}
                payWhatYouWant={slideHitPayWhatYouWant}
              />
            }

            {showTimer &&
              <NewCountdownTimer
                cardTimer
                value={slideHitAvailableUntil * 1000}
              />
            }

            {showAddButton &&
              <AddToCartBtn
                newIcon
                showInCartLink
                hideButtonText
                id={slideHitProductId}
                slug={slideHitSlug}
                voucherToAdd={slideHitVoucherToAdd}
                onAddToCart={() => logClick('AddToCartButton')}
              />
            }
            {!showAddButton &&
              <LinkTag
                url={slideUrl}
                externalLink={slideExternalLink}
                className="AddToCartBtn AddToCartBtn--iconOnly btn btn-primary"
                onClick={() => logClick()}
              >
                <div className="cart-btn-icon">
                  <FontAwesomeIcon icon={faCircleRight} title="View" />
                </div>
              </LinkTag>
            }
          </div>

          {showLabel &&
            <div className={`CarouselCard__categoryLabel CarouselCard__categoryLabel--${slideType}`}>
              {slideType === 'categorySale' &&
                <FormattedMessage id="carousel-card.label.category" defaultMessage="Category Sale" />
              }
              {slideType === 'siteWideSale' &&
                <FormattedMessage id="carousel-card.label.sitewide" defaultMessage="Sitewide Sale" />
              }
              {slideType === 'publisherSale' &&
                <FormattedMessage id="carousel-card.label.publishersale" defaultMessage="Publisher Sale" />
              }
              {slideType === 'franchiseSale' &&
                <FormattedMessage id="carousel-card.label.franchisesale" defaultMessage="Franchise Sale" />
              }
            </div>
          }

          {showPlatforms &&
            <PlatformIcons hit={slideHit} colorOverride={isHovering ? 'light' : ''} />
          }
        </div>
      </div>
    </article>
  );
}

CarouselCard.propTypes = {
  card: PropTypes.shape({}),
  size: PropTypes.string,
  skeleton: PropTypes.bool,
  cardNumber: PropTypes.number,
  FeaturedTopSellerCarousel: PropTypes.bool,
};

CarouselCard.defaultProps = {
  size: '',
  card: {},
  skeleton: false,
  cardNumber: null,
  FeaturedTopSellerCarousel: false,
};
